import React from "react";
import styled from "@emotion/styled";
import { Body2, Header4 } from "@iventis/styles/src/components/texts";
import { SsoConfigForm } from "@iventis/components";
import { useFunctionality } from "@iventis/utilities";
import { sectionalMargin } from "@iventis/styles";
import { toast } from "@iventis/toasts/src/toast";
import { IventisErrorBoundaryWithToast } from "@iventis/error-boundaries";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SsoConfig } from "@iventis/domain-model/model/ssoConfig";
import { SsoConfigApiUpdateRequest } from "@iventis/domain-model/model/ssoConfigApiUpdateRequest";
import { getIventisEnvironmentData, permissionsApi } from "@iventis/api/src/api";
import { NoAccessBanner } from "@iventis/permissions/src/upgrade-limit-overlay";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations";

export const ProjectSsoConfig: React.FC<{ projectId: string }> = ({ projectId }) => {
    const translate = useIventisTranslate();
    const { subDomain, apiUrl, apiProtocol } = getIventisEnvironmentData();
    const apiBaseUrl = `${apiProtocol}${subDomain}.${apiUrl}`;
    const { status, data: ssoConfig } = useQuery<SsoConfig>(["sso-config", projectId], () =>
        permissionsApi.get<SsoConfig>(`projects/${projectId}/sso-config`).then((res) => res.data)
    );
    const queryClient = useQueryClient();
    const { mutateAsync } = useMutation(
        async (config: SsoConfigApiUpdateRequest) => {
            await permissionsApi.put<SsoConfigApiUpdateRequest>(`projects/${projectId}/sso-config`, config, {
                customErrorMessage: {
                    title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.settings3.sso.title) }),
                    message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.settings3.sso.title) }),
                },
            });
            toast.success({ title: translate(Content.common.success), message: translate(Content.settings3.sso.saved) });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(["sso-config", projectId]);
            },
        }
    );
    const functionality = useFunctionality();
    return (
        <>
            <StyledContainer>
                <ProjectDetailsHeader>
                    <Header4>{translate(Content.settings3.sso.title)}</Header4>
                    <Body2>{translate(Content.settings3.sso.page_content)}</Body2>
                </ProjectDetailsHeader>
                {!functionality.sso && (
                    <StyledNoAccessBanner>
                        <NoAccessBanner upgradeType="sso" className="no-access-banner" />
                    </StyledNoAccessBanner>
                )}
                {functionality.sso && (
                    <IventisErrorBoundaryWithToast toast={toast}>
                        <SsoConfigForm ssoConfig={ssoConfig} loading={status === "loading"} onSubmit={mutateAsync} apiBaseUrl={apiBaseUrl} />
                    </IventisErrorBoundaryWithToast>
                )}
            </StyledContainer>
        </>
    );
};

const StyledNoAccessBanner = styled.div`
    .no-access-banner {
        width: 100%;
    }
`;

const ProjectDetailsHeader = styled.div`
    margin-bottom: ${sectionalMargin};
`;

const StyledContainer = styled.div`
    hight: 100%;
    width: 100%;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    padding: ${sectionalMargin};
`;
